import copyToClipboard from 'copy-to-clipboard';
import Image from 'next/image';
import Link from 'next/link';
import { useSnackbar } from 'notistack';

import googlePin from '@/assets/google-pin.svg';
import ImageWithFallback from '@/components/ImageWithFallback';
import copy from '@/components/Pages/VenueDetail/assets/copy.svg';
import useViewport from '@/hooks/useViewport';
import { splitAddress } from '@/utils';

export type EventVenueItemProps = {
  venueImageUrl: string;
  mapUrl: string;
  venueName: string;
  venueAddress: string;
  venueId: string;
};

const MobileEventVenueItem = (props: EventVenueItemProps) => {
  const { venueImageUrl, venueName, venueAddress, mapUrl, venueId } = props;
  const { enqueueSnackbar } = useSnackbar();

  return (
    <div className={`flex w-full items-start gap-[16px]`}>
      <Link href={`/venues/${venueId}`}>
        <a>
          <ImageWithFallback
            key={venueImageUrl}
            placeholder="blur"
            blurDataURL={venueImageUrl}
            width={56}
            height={56}
            src={venueImageUrl}
            alt={`${venueName} image`}
            className={`rounded-[28px] object-cover`}
          />
        </a>
      </Link>
      <div className={`flex flex-col gap-[6px]`}>
        <Link href={`/venues/${venueId}`}>
          <a>
            <p className={`font-roboto text-h5 font-normal`}>{venueName}</p>
          </a>
        </Link>
        <div
          className={`flex items-end gap-[2px] text-b3 font-normal text-green-200`}
        >
          <div className={``}>
            {splitAddress(venueAddress).map((text, idx) => (
              <p key={idx}>{text}</p>
            ))}
          </div>
          <button
            className={`h-full flex-col justify-end p-[4px]`}
            onClick={() => {
              copyToClipboard(venueAddress, {
                debug: true,
                message: `Press #{key} to copy`,
              });
              enqueueSnackbar(`Address copied`);
            }}
          >
            <Image width={16} height={16} src={copy} alt="copy" />
          </button>
        </div>
        <Link href={mapUrl}>
          <a>
            <button className={`mt-[18px] flex`}>
              <div
                className={`flex items-center justify-center gap-[10px] rounded-[12px] bg-gray-10 py-[15px] px-[16px] text-h5 font-normal text-gray-200`}
              >
                <Image
                  width={15}
                  height={22}
                  src={googlePin}
                  alt="google-pin"
                />
                Open in Maps
              </div>
            </button>
          </a>
        </Link>
      </div>
    </div>
  );
};

const DesktopEventVenueItem = (props: EventVenueItemProps) => {
  const { venueImageUrl, venueName, venueAddress, mapUrl, venueId } = props;
  const { enqueueSnackbar } = useSnackbar();

  return (
    <div className={`flex w-full items-start gap-[32px]`}>
      <Link href={`/venues/${venueId}`}>
        <a>
          <div className={`h-[80px] shrink-0 grow-0 basis-[80px]`}>
            <ImageWithFallback
              key={venueImageUrl}
              placeholder="blur"
              blurDataURL={venueImageUrl}
              width={80}
              height={80}
              src={venueImageUrl}
              alt={`${venueName} image`}
              className={`rounded-[80px] object-cover`}
            />
          </div>
        </a>
      </Link>
      <div className={`flex flex-col gap-[6px]`}>
        <Link href={`/venues/${venueId}`}>
          <a>
            <p className={`font-roboto text-h4 font-medium`}>{venueName}</p>
          </a>
        </Link>
        <div
          className={`flex items-end gap-[2px] text-b2 font-normal text-green-200`}
        >
          <div className={``}>
            {splitAddress(venueAddress).map((text, idx) => (
              <p key={idx}>{text}</p>
            ))}
          </div>
          <button
            className={`h-full flex-col justify-end p-[4px]`}
            onClick={() => {
              copyToClipboard(venueAddress, {
                debug: true,
                message: `Press #{key} to copy`,
              });
              enqueueSnackbar(`Address copied`);
            }}
          >
            <Image width={16} height={16} src={copy} alt="copy" />
          </button>
        </div>
        <Link href={mapUrl}>
          <a>
            <button className={`mt-[18px] flex`}>
              <div
                className={`flex items-center justify-center gap-[10px] rounded-[12px] bg-gray-10 py-[15px] px-[16px] text-h5 font-normal text-gray-200`}
              >
                <Image
                  width={15}
                  height={22}
                  src={googlePin}
                  alt="google-pin"
                />
                Open in Maps
              </div>
            </button>
          </a>
        </Link>
      </div>
    </div>
  );
};

const EventVenueItem = (props: EventVenueItemProps) => {
  const {} = props;
  const viewport = useViewport();

  if (viewport == `mobile`) {
    return <MobileEventVenueItem {...props} />;
  }
  if (viewport == `tablet` || viewport == `desktop`) {
    return <DesktopEventVenueItem {...props} />;
  }

  return null;
};

export default EventVenueItem;
